<template>
  <div class="faraday-disk-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$U = 1/2 B ω R²$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
          <g transform="translate(160, 100)">
             <!-- Magnet Background (N Pole) -->
             <rect x="-40" y="-80" width="80" height="30" fill="#ef4444" stroke="#7f1d1d" stroke-width="2" rx="4" />
             <text x="-6" y="-60" fill="#f8fafc" font-size="14" font-weight="bold">N</text>
             
             <!-- Magnetic Field Lines (Into the page or from N to S) -->
             <g stroke="rgba(255,255,255,0.15)" stroke-width="1.5" stroke-dasharray="4,4">
               <line v-for="i in 5" :key="'m'+i" :x1="-30 + i*10" y1="-50" :x2="-30 + i*10" y2="80" />
             </g>

             <!-- The Rotating Disk -->
             <g :transform="`rotate(${angle})`">
                <circle r="60" fill="rgba(217, 119, 6, 0.2)" stroke="#d97706" stroke-width="3" />
                <line x1="0" y1="-60" x2="0" y2="60" stroke="rgba(217, 119, 6, 0.4)" stroke-width="1" />
                <line x1="-60" y1="0" x2="60" y2="0" stroke="rgba(217, 119, 6, 0.4)" stroke-width="1" />
                <!-- Drifting Electrons due to Lorentz force -->
                <circle v-for="i in 3" :key="'e'+i" :cx="0" :cy="ePos[i-1]" r="3" fill="#38bdf8" />
             </g>
             
             <!-- Magnet Foreground (S Pole) -->
             <rect x="-40" y="50" width="80" height="30" fill="#3b82f6" stroke="#1e3a8a" stroke-width="2" rx="4" opacity="0.4" />
             <text x="-5" y="70" fill="#f8fafc" font-size="14" font-weight="bold">S</text>

             <!-- Circuit Brushes (Axle & Edge) -->
             <circle cx="0" cy="0" r="4" fill="#64748b" /> <!-- Center brush -->
             <rect x="58" y="-5" width="6" height="10" fill="#64748b" rx="2" /> <!-- Edge brush -->

             <!-- Wire path -->
             <path d="M 0,0 L 0,-20 L 100,-20 L 100,40" fill="none" stroke="#94a3b8" stroke-width="2" />
             <path d="M 64,0 L 140,0 L 140,40" fill="none" stroke="#94a3b8" stroke-width="2" />
             
             <!-- Galvanometer Component -->
             <g transform="translate(120, 60)">
                <rect x="-25" y="-20" width="50" height="40" rx="4" fill="#1e293b" stroke="#475569" stroke-width="2" />
                <!-- Scale marks -->
                <line x1="-15" y1="-5" x2="-12" y2="0" stroke="#94a3b8" />
                <line x1="0" y1="-8" x2="0" y2="-2" stroke="#94a3b8" />
                <line x1="15" y1="-5" x2="12" y2="0" stroke="#94a3b8" />
                
                <circle cx="0" cy="15" r="3" fill="#cbd5e1" />
                <line x1="0" y1="15" :x2="needleX" :y2="needleY" stroke="#ef4444" stroke-width="2" stroke-linecap="round" />
                <text x="0" y="10" text-anchor="middle" fill="#f8fafc" font-size="9">V (DC)</text>
             </g>
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>圆盘角速度 ω</label>
              <span class="val">{{ rpmRaw.toFixed(1) }} <small>rad/s</small></span>
           </div>
           <div class="hud-item orange">
              <label>感应电动势 U</label>
              <span class="val">{{ voltage.toFixed(2) }} <small>mV</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">调节旋转角速度 ω (正负代表顺/逆时针)</label>
                    <el-slider v-model="rpmRaw" :min="-50" :max="50" :step="1" size="small"></el-slider>
                 </div>
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">磁感应强度 B</label>
                    <el-slider v-model="fieldStrength" :min="1" :max="10" :step="1" size="small"></el-slider>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>法拉第圆盘是世界上第一台发电机（直流单极发电机）。我们在边缘和圆心处用电刷引出电路。当铜盘在匀强磁场 <code>B</code> 中以角速度 <code>ω</code> 旋转时，盘内每一个载流子（自由电子）均因随盘运动而受到指向半径方向的<strong>洛伦兹力</strong>。这会在中心与边缘之间产生稳定的感应电动势 <code>U = ½ B ω R²</code>（这里为纯直流，不需换向器）。拖动滑块改变转向，可观察到电压表指针反偏。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FaradayDiskViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      rpmRaw: 20, // Negative for reverse 
      fieldStrength: 5,
      angle: 0,
      ePos: [10, 30, 50],
      timer: null
    }
  },
  computed: {
    voltage() {
       // U = 0.5 * B * omega * R^2 
       // Scaled for UI presentation
       return 0.5 * this.fieldStrength * this.rpmRaw * Math.pow(1.2, 2);
    },
    needleX() {
       // map voltage (-360 to +360) to angle -pi/4 to pi/4
       let maxV = 0.5 * 10 * 50 * 1.44; 
       let ratio = this.voltage / maxV; // -1 to 1
       let theta = -Math.PI/2 + (ratio * Math.PI/3);
       return Math.cos(theta) * 20;
    },
    needleY() {
       let maxV = 0.5 * 10 * 50 * 1.44; 
       let ratio = this.voltage / maxV;
       let theta = -Math.PI/2 + (ratio * Math.PI/3);
       return 15 + Math.sin(theta) * 20;
    }
  },
  methods: {
    animate() {
       // rotate disk
       this.angle += this.rpmRaw * 0.1;
       
       // electron motion visual effect
       let driftSpeed = (this.voltage !== 0) ? (this.voltage > 0 ? 0.5 : -0.5) : 0;
       for (let i=0; i<3; i++) {
          this.ePos[i] += driftSpeed;
          if (this.ePos[i] > 60) this.ePos[i] = 0;
          if (this.ePos[i] < 0) this.ePos[i] = 60;
       }
       
       this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { if(this.timer) cancelAnimationFrame(this.timer); }
}
</script>

<style scoped lang="scss">
.faraday-disk-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
